import type {IssueAgentTask} from '@github-ui/agent-sessions/hooks/use-issue-agent-tasks'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {isSessionState} from '@github-ui/agent-sessions/types/session'

import {assertNever} from './assert-never'
import {TASK_NOUN} from './constants'

// SummaryGroup is a breakdown of session states into buckets that we use to render the agent task header.
// Below we define maps of mission-control defined session states to the summaries we use in the header
// and vice versa.
type SummaryGroup = 'needs_input' | 'running' | 'failed' | 'complete'

const SUMMARY_GROUP_PRIORITY: SummaryGroup[] = ['needs_input', 'running', 'failed', 'complete']

const SUMMARY_GROUP_LABELS: Record<SummaryGroup, string> = {
  needs_input: 'needs input',
  running: 'running',
  failed: 'failed',
  complete: 'complete',
}

const SUMMARY_GROUP_TO_SESSION_STATE: Record<SummaryGroup, SessionState> = {
  needs_input: 'waiting_for_user',
  running: 'in_progress',
  failed: 'failed',
  complete: 'completed',
}

function getSummaryGroupText(status: string | undefined): SummaryGroup {
  const normalizedStatus: SessionState = status && isSessionState(status) ? status : 'in_progress'

  switch (normalizedStatus) {
    case 'waiting_for_user':
      return 'needs_input'
    case 'in_progress':
    case 'queued':
    case 'idle':
    case 'cancelled':
      return 'running'
    case 'failed':
    case 'timed_out':
      return 'failed'
    case 'completed':
      return 'complete'
    default:
      assertNever(normalizedStatus)
  }
}

export type Summary = {
  summaryText: string
  summaryStatus: SessionState
}

export function getSummary(tasks: IssueAgentTask[]): Summary {
  // We don't show the tasks Token when there are 0 tasks on page load, however it's possible that tasks could later change through an useAlive update.
  if (tasks.length === 0) {
    return {summaryText: `0 ${TASK_NOUN}s`, summaryStatus: 'completed'}
  }

  const counts: Record<SummaryGroup, number> = {
    needs_input: 0,
    running: 0,
    failed: 0,
    complete: 0,
  }

  for (const task of tasks) {
    const group = getSummaryGroupText(task.state)
    counts[group]++
  }

  const presentGroups = SUMMARY_GROUP_PRIORITY.filter(group => counts[group] > 0)
  const [primary] = presentGroups

  // Every task maps to a group, so presentGroups is guaranteed non-empty when tasks.length > 0.
  // This check only satisfies the type narrowing from `SummaryGroup | undefined` to `SummaryGroup`.
  if (!primary) {
    return {summaryText: `${tasks.length} ${TASK_NOUN}${tasks.length === 1 ? '' : 's'}`, summaryStatus: 'in_progress'}
  }

  const summaryStatus = SUMMARY_GROUP_TO_SESSION_STATE[primary]
  const primaryCount = counts[primary]
  const primaryLabel = SUMMARY_GROUP_LABELS[primary]
  const primaryText = `${primaryCount} ${TASK_NOUN}${primaryCount === 1 ? '' : 's'} ${primaryLabel}`

  if (presentGroups.length === 1) {
    return {summaryText: primaryText, summaryStatus}
  }

  if (presentGroups.length === 2) {
    const [, secondary] = presentGroups
    if (!secondary) return {summaryText: primaryText, summaryStatus}

    const secondaryCount = counts[secondary]
    return {
      summaryText: `${primaryText}, ${secondaryCount} ${SUMMARY_GROUP_LABELS[secondary]}`,
      summaryStatus,
    }
  }

  const otherCount = presentGroups.slice(1).reduce((total, group) => total + counts[group], 0)
  return {
    summaryText: `${primaryText}, ${otherCount} other${otherCount === 1 ? '' : 's'}`,
    summaryStatus,
  }
}
